import { useLazyQuery } from "@apollo/client";
import {
  GET_COMPLIANCE_POLICIES,
  GetCompliancePoliciesData,
  GetCompliancePoliciesVars,
} from "@src/driverCompliancePolicies/graphql/queries";
import {
  resolveCascade,
  type CascadeResult,
} from "@src/driverCompliancePolicies";
import { contributingPackageTypeIds } from "@src/common/utils/packageTypeDefaults";
import type { DuplicateShipmentSource } from "./useDuplicateShipmentSource";

/**
 * Resolves the driver certification cascade for a duplicated order.
 *
 * A duplicate is a brand new order, so its certifications are resolved from the copied
 * stops/customer/package type rather than copied from the source order's own saved policy -
 * that policy can be missing (order creation only saves one when the cascade is non-empty) or
 * stale (courier/customer/location/package rules can change after the source order was created).
 * This is the same cascade order entry runs; duplication just never ran it.
 */
export function useDuplicateOrderDriverCredentials() {
  const [fetchCompliancePolicies] = useLazyQuery<
    GetCompliancePoliciesData,
    GetCompliancePoliciesVars
  >(GET_COMPLIANCE_POLICIES, { fetchPolicy: "network-only" });

  function resolveDriverCredentials(
    source: DuplicateShipmentSource,
  ): Promise<CascadeResult> {
    // Every stop, not just dropoffs: a location's certification applies to visiting it, and a
    // Return duplicate turns the source dropoff into the new order's pickup.
    const locationIds = source.stops
      .map((stop) => stop.locationId)
      .filter((locationId): locationId is string => Boolean(locationId));
    const packageTypeIds = contributingPackageTypeIds(
      source.packageType?.id,
      undefined,
    );
    const endCustomerId = source.order.endCustomer.id || undefined;

    return fetchCompliancePolicies({
      variables: {
        courierId: source.courierId,
        entityIds: [
          source.courierId,
          ...(endCustomerId ? [endCustomerId] : []),
          ...locationIds,
          ...packageTypeIds,
        ],
      },
    }).then((res) => {
      // Fail the duplicate rather than create an order that silently understates what it
      // requires — an order missing its certifications is the bug this exists to prevent.
      if (res.error) throw res.error;
      return resolveCascade(
        source.courierId,
        endCustomerId,
        locationIds,
        res.data?.driverCompliancePolicies ?? [],
        packageTypeIds,
      );
    });
  }

  return { resolveDriverCredentials };
}
